<template>
  <div
    :class="`repartition-bar-vertical__${data.positive ? 'positive' : 'negative'}`"
    class="repartition-bar-vertical"
  >
    <div
      :style="[dimensionStyles]"
      class="repartition-bar-vertical-graph"
    >
      <div class="repartition-bar-vertical-graph-list-item">
        <RepartitionBarItem
          v-for="item of items"
          :key="item.code"
          orientation="vertical"
          :percentage="item.percentage"
          :color="item.color"
          :disabled="isItemDisabled(item)"
        >
          <RepartitionBarItemResume
            :visible="item.code === value"
            :item="item"
            :unit="unit"
            :positive-color="positiveColor"
            :negative-color="negativeColor"
            location="right"
            @updateVisibility="onUpdateResumeVisibility(item, $event)"
          />
        </RepartitionBarItem>
      </div>

      <div
        class="repartition-bar-vertical-graph-value"
        :style="[colorData.colorStyles]"
        :class="[colorData.colorClasses]"
      />

      <div
        class="repartition-bar-vertical-graph-empty-item"
        :style="[emptyColorData.colorStyles]"
        :class="[emptyColorData.colorClasses]"
      />
    </div>

    <div class="repartition-bar-vertical-legends">
      <GraphLegend
        v-for="item of itemsForLegends"
        :key="item.code"
        :legend="item"
      />
    </div>
  </div>
</template>

<script>
import orderBy from 'lodash/orderBy'
import { convertToUnit } from 'vuetify/lib/util/helpers'
import { PALETTE_REPARTITION, couleursPourEffectif } from '@/utils/palette-repartition'
import { getBackgroundColorData, getTextColorData } from './colorUtils'
import GraphLegend from './graph-legend.vue'
import RepartitionBarItem from './repartition-bar-item.vue'
import RepartitionBarItemResume from './repartition-bar-item-resume.vue'

const HAUTEUR_HABILLAGE = 55
const HAUTEUR_BARRES_MIN = 16

export default {
  name: 'RepartitionBarVertical',

  components: {
    GraphLegend,
    RepartitionBarItemResume,
    RepartitionBarItem
  },

  props: {
    value: {
      type: String,
      default: null
    },
    data: {
      type: Object,
      required: true
    },
    unit: {
      type: String,
      default: null
    },
    width: {
      type: [String, Number],
      default: 192
    },
    height: {
      type: [String, Number],
      default: 212
    },
    positiveColor: {
      type: String,
      default: '#01713C'
    },
    negativeColor: {
      type: String,
      default: 'warning'
    },
    emptyColor: {
      type: String,
      default: 'grey lighten-4'
    },
    palette: {
      type: Array,
      default: () => PALETTE_REPARTITION
    }
  },

  emits: ['input'],

  computed: {
    items () {
      return this.data.items.map((item, index) => ({
        ...item,
        color: item.color != null ? item.color : this.colors[index]
      }))
    },
    itemsForLegends () {
      return orderBy(this.items, 'percentage', 'desc')
    },
    dimensionStyles () {
      return {
        width: convertToUnit(this.width),
        '--hauteur-barres': convertToUnit(Math.max(this.height - HAUTEUR_HABILLAGE, HAUTEUR_BARRES_MIN))
      }
    },
    colorData () {
      return getTextColorData(
        this.data.positive ? this.positiveColor : this.negativeColor
      )
    },

    emptyColorData () {
      return getBackgroundColorData(this.emptyColor)
    },
    colors () {
      return couleursPourEffectif(this.data.items.length, this.palette)
    }
  },

  methods: {
    isItemDisabled (item) {
      return this.value != null && this.value !== item.code
    },

    onUpdateResumeVisibility (item, visible) {
      let selectedCode
      if (visible) {
        selectedCode = item.code
      } else {
        if (this.value === item.code) {
          selectedCode = null
        }
      }

      this.$emit('input', selectedCode)
    }
  }
}
</script>

<style lang="scss" scoped>
.repartition-bar-vertical {
  display: flex;
  margin-left: 16px;
  gap: 56px;
  max-width: 100%;
  min-width: 0;

  .repartition-bar-vertical-legends {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
    min-width: 0;
  }

  .repartition-bar-vertical-graph {
    display: flex;
    flex-direction: column;
    max-width: 100%;
    min-width: 0;
    padding: 12px;
    border-radius: 12px;
    box-shadow:
      0 3px 1px -2px var(--v-shadow-key-umbra-opacity, rgba(0, 0, 0, 0.2)),
      0 2px 2px 0 var(--v-shadow-key-penumbra-opacity, rgba(0, 0, 0, 0.14)),
      0 1px 5px 0 var(--v-shadow-key-ambient-opacity, rgba(0, 0, 0, 0.12));
    background-color: #ffffff;

    .repartition-bar-vertical-graph-list-item {
      display: flex;
      flex-direction: column;
      gap: 1px;
      width: 100%;
      height: 100%;
      border-radius: 7px;
      overflow: hidden;
    }

    .repartition-bar-vertical-graph-value {
      height: 3px;
      position: relative;

      &::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: -28px;
        right: -28px;
        background-color: currentColor;
      }
    }

    .repartition-bar-vertical-graph-empty-item {
      height: 40px;
    }
  }
  @media (max-width: 960px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    margin-left: 0;

    .repartition-bar-vertical-graph {
      align-self: center;
    }

    .repartition-bar-vertical-legends {
      margin-left: 12px;
    }
    .repartition-bar-vertical-graph {
      .repartition-bar-vertical-graph-list-item {
        height: var(--hauteur-barres);
        flex: 0 0 auto;
      }

      .repartition-bar-vertical-graph-value,
      .repartition-bar-vertical-graph-empty-item {
        flex: 0 0 auto;
      }
    }
  }

  &.repartition-bar-vertical__positive {
    .repartition-bar-vertical-graph {
      padding-bottom: 0;

      &,
      .repartition-bar-vertical-graph-list-item {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
  }

  &.repartition-bar-vertical__negative {
    .repartition-bar-vertical-graph {
      padding-top: 0;
      flex-direction: column-reverse;

      &,
      .repartition-bar-vertical-graph-list-item {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }
    }
  }
}
</style>
